import type { TableRow } from "@/components/pdf-viewer";
import type {
  Rect,
  SourceRegion,
} from "@/components/pdf-viewer/regionGeometry";
import type { AttachedFile } from "@/app/api/types/email";
import type { PreOrderFlow } from "@/app/api/types/preOrder";
import type { BoundingBox, ImageBand } from "../types/delineation.types";
import {
  attachmentFilenameMatchesSourceDoc,
  attachmentKeyMatchesSourceDoc,
  normalizedFilename,
} from "../utils/sourceDocIdentity";

export type { Rect, SourceRegion };

/** Status → overlay color mapping */
const STATUS_COLORS: Record<number, string> = {
  0: "#FBBF24", // pending - amber
  1: "#34D399", // confirmed - green
  2: "#F87171", // flagged - red
  3: "#9CA3AF", // cancelled - gray
};

// Lighter fill so the document text stays legible behind the band; the thin
// border drawn by the viewers keeps the highlighted region clearly delimited.
const ALPHA_HOVERED = "4D";

/** Percentage padding around each PAINTED box so a highlight does not clip
 *  glyphs. Deliberately absent from the hit-test rects — design §2.4. */
const PAD = 0.5;

export interface SourceRegions {
  tableRows: TableRow[];
  imageBands: ImageBand[];
  excelRows: number[];
  flowIdsByOrdinal: Map<number, string[]>;
  pdfRegions: SourceRegion[];
  photoRegions: SourceRegion[];
}

export const EMPTY_SOURCE_REGIONS: SourceRegions = {
  tableRows: [],
  imageBands: [],
  excelRows: [],
  flowIdsByOrdinal: new Map(),
  pdfRegions: [],
  photoRegions: [],
};

export interface StrongShapes {
  tableRows: TableRow[];
  imageBands: ImageBand[];
  ordinals: number[];
}

const EMPTY_STRONG_SHAPES: StrongShapes = {
  tableRows: [],
  imageBands: [],
  ordinals: [],
};

/**
 * The painted shapes for just these flows — the strong layer when the
 * pointer is over their region in the document (STK-2338 reverse hover).
 * `regions` is already tagged with `flowId` and coloured by status; this
 * only filters. Stable empty result for an empty `flowIds` so callers
 * (a `useMemo` keyed on the store) don't churn while nothing is hovered.
 */
export function strongShapesForFlows(
  regions: SourceRegions,
  flowIds: readonly string[],
): StrongShapes {
  if (flowIds.length === 0) return EMPTY_STRONG_SHAPES;
  const idSet = new Set(flowIds);
  const ordinals = new Set<number>();
  for (const [ordinal, ids] of regions.flowIdsByOrdinal) {
    if (ids.some((id) => idSet.has(id))) ordinals.add(ordinal);
  }
  return {
    tableRows: regions.tableRows.filter(
      (row) => row.flowId && idSet.has(row.flowId),
    ),
    imageBands: regions.imageBands.filter(
      (band) => band.flowId && idSet.has(band.flowId),
    ),
    ordinals: [...ordinals].sort((a, b) => a - b),
  };
}

export interface DocMatchContext {
  previewedAttachment: AttachedFile;
  attachmentBase: string;
  nameIsAmbiguous: boolean;
}

/** Extensions the AI pipeline may rewrite between the delineation URL and the
 *  attachment (a PDF page cited as a rendered PNG), stripped on BOTH sides
 *  before the base names are compared. */
const REWRITABLE_EXT = /\.(pdf|png|jpg|jpeg)$/i;

/** The final path segment of a document URL, query/hash stripped and
 *  percent-decoded — the same canonical form `sourceDocIdentity` compares. */
function urlBaseName(documentUrl: string): string {
  const segment = documentUrl.split("/").pop()?.split(/[?#]/)[0] ?? "";
  return segment ? normalizedFilename(segment).replace(REWRITABLE_EXT, "") : "";
}

/** Does a delineation item's document_url refer to the previewed attachment by
 *  NAME? Exact filename identity first (the canonical strict matcher), then
 *  base-name EQUALITY so a pdf cited as its rendered png still resolves.
 *
 *  Deliberately never a substring test: `docUrl.includes(name)` resolved
 *  `report-order.xlsx` to the attachment `order.xlsx` and painted one
 *  document's regions over the other (cubic/greptile P1). This mirrors the ban
 *  documented on `attachmentFilenameMatchesSourceDoc`. */
function matchesAttachmentName(
  documentUrl: string,
  ctx: DocMatchContext,
): boolean {
  if (
    attachmentFilenameMatchesSourceDoc(
      documentUrl,
      ctx.previewedAttachment.name,
    )
  ) {
    return true;
  }
  const base = urlBaseName(documentUrl);
  return !!base && !!ctx.attachmentBase && base === ctx.attachmentBase;
}

/** Does a delineation item's document belong to the previewed attachment?
 *  1) strong storage-key identity (disambiguates same-named docs);
 *  2) else if the name is ambiguous, key identity is REQUIRED → no match;
 *  3) else fall back to the name match, so delineation URLs written before the
 *     storage-key change (no key) still render. */
function itemMatchesPreviewedDoc(
  documentUrl: string,
  ctx: DocMatchContext,
): boolean {
  if (attachmentKeyMatchesSourceDoc(documentUrl, ctx.previewedAttachment)) {
    return true;
  }
  if (ctx.nameIsAmbiguous) return false;
  return matchesAttachmentName(documentUrl, ctx);
}

export function buildDocMatchContext(
  previewedAttachment: AttachedFile,
  emailAttachments: AttachedFile[],
): DocMatchContext {
  return {
    previewedAttachment,
    attachmentBase: normalizedFilename(previewedAttachment.name).replace(
      REWRITABLE_EXT,
      "",
    ),
    // When two attachments share the previewed display name, the filename can't
    // say WHICH document is open, so a fuzzy fallback would draw the wrong doc's
    // boxes. In that case require a storage-KEY match (STK-1269/STK-1360 guard).
    nameIsAmbiguous:
      emailAttachments.filter(
        (a) =>
          normalizedFilename(a.name) ===
          normalizedFilename(previewedAttachment.name),
      ).length > 1,
  };
}

/** First band in document order — lowest page, then topmost y. Pure. */
export function pickFocusRowId(rows: TableRow[]): string | null {
  let best: TableRow | null = null;
  for (const row of rows) {
    if (
      !best ||
      row.pageNumber < best.pageNumber ||
      (row.pageNumber === best.pageNumber && row.y < best.y)
    ) {
      best = row;
    }
  }
  return best?.id ?? null;
}

interface WalkSinks {
  tableRows: TableRow[];
  imageBands: ImageBand[];
  pdfRegions: SourceRegion[];
  photoRegions: SourceRegion[];
}

interface PushBoundingBoxesArgs {
  boxes: BoundingBox[];
  flowId: string;
  itemIdx: number;
  color: string;
  startBandIdx: number;
  angle: number | undefined;
  /** Output sinks — appended to in place. */
  out: WalkSinks;
}

/** Push a delineation item's bounding boxes as PDF table rows + photo image
 *  bands + hit-test regions; returns the next band index to keep band ids
 *  unique across items. Named-object argument — `flowId` and `color` are
 *  adjacent strings, transposable with no type error as positional params. */
function pushBoundingBoxes({
  boxes,
  flowId,
  itemIdx,
  color,
  startBandIdx,
  angle,
  out,
}: PushBoundingBoxesArgs): number {
  let bandIdx = startBandIdx;
  boxes.forEach((box, idx) => {
    const raw: Rect = {
      x: box.x1 * 100,
      y: box.y1 * 100,
      width: (box.x2 - box.x1) * 100,
      height: (box.y2 - box.y1) * 100,
    };
    const page = box.page || 1;
    const x = Math.max(0, raw.x - PAD);
    const y = Math.max(0, raw.y - PAD);

    out.tableRows.push({
      id: `delineation-${flowId}-${itemIdx}-${idx}`,
      pageNumber: page,
      x,
      y,
      width: Math.min(100 - x, raw.width + PAD * 2),
      height: Math.min(100 - y, raw.height + PAD * 2),
      color,
      alpha: ALPHA_HOVERED,
      flowId,
      ...(angle ? { angle } : {}),
    });
    out.imageBands.push({
      id: `band-${flowId}-${bandIdx}`,
      y: raw.y,
      height: raw.height,
      color,
      flowId,
      ...(angle ? { angle } : {}),
    });
    out.pdfRegions.push({
      flowId,
      page,
      rect: raw,
      ...(angle ? { angle } : {}),
    });
    // Photo bands are painted full width, so their hit area must be too.
    out.photoRegions.push({
      flowId,
      page: 1,
      rect: { x: 0, y: raw.y, width: 100, height: raw.height },
      ...(angle ? { angle } : {}),
    });
    bandIdx += 1;
  });
  return bandIdx;
}

/** One `flow_metadata.delineation` entry, as loosely typed as it arrives. */
interface DelineationEntry {
  document_url?: string;
  bounding_boxes?: unknown;
  rows?: unknown;
  angle?: unknown;
}

const flowColor = (status: number | null | undefined): string =>
  STATUS_COLORS[status ?? 0] || "#FBBF24";

/** This flow's delineation entries that belong to the previewed document,
 *  paired with their ORIGINAL index (band ids embed it, so filtering must not
 *  renumber them). */
function matchedEntries(
  flow: PreOrderFlow,
  ctx: DocMatchContext,
): Array<[number, DelineationEntry]> {
  const delineation = (flow.flow_metadata as { delineation?: unknown })
    ?.delineation;
  if (!Array.isArray(delineation)) return [];
  return [...(delineation as DelineationEntry[]).entries()].filter(
    ([, item]) =>
      !!item?.document_url && itemMatchesPreviewedDoc(item.document_url, ctx),
  );
}

/** Record this flow against every ordinal (excel/html/text row) it cites. */
function pushOrdinals(
  rows: unknown,
  flowId: string,
  flowIdsByOrdinal: Map<number, string[]>,
): void {
  if (!Array.isArray(rows)) return;
  for (const ordinal of rows as number[]) {
    const ids = flowIdsByOrdinal.get(ordinal) ?? [];
    if (!ids.includes(flowId)) ids.push(flowId);
    flowIdsByOrdinal.set(ordinal, ids);
  }
}

/**
 * Walk every flow's `flow_metadata.delineation`, keeping only the items that
 * belong to the previewed document, and emit both the painted shapes and the
 * raw hit regions. The single source of geometry for BOTH hover directions.
 */
export function collectRegionsForFlows(
  flows: PreOrderFlow[],
  ctx: DocMatchContext,
): SourceRegions {
  const out: WalkSinks = {
    tableRows: [],
    imageBands: [],
    pdfRegions: [],
    photoRegions: [],
  };
  const flowIdsByOrdinal = new Map<number, string[]>();
  let bandIdx = 0;

  for (const flow of flows) {
    for (const [itemIdx, item] of matchedEntries(flow, ctx)) {
      if (Array.isArray(item.bounding_boxes)) {
        bandIdx = pushBoundingBoxes({
          boxes: item.bounding_boxes as BoundingBox[],
          flowId: flow.id,
          itemIdx,
          color: flowColor(flow.status),
          startBandIdx: bandIdx,
          angle: typeof item.angle === "number" ? item.angle : undefined,
          out,
        });
      }
      pushOrdinals(item.rows, flow.id, flowIdsByOrdinal);
    }
  }

  return {
    ...out,
    excelRows: [...flowIdsByOrdinal.keys()].sort((a, b) => a - b),
    flowIdsByOrdinal,
  };
}
